import api from './client';

// =============================================================================
// CHASTITY API (Spec 035)
// =============================================================================

export interface ChastityLock {
  id: number;
  publicId: string;
  organizationId: number;
  wearerUserId: string;  // Public UUID (from CAMEL_CASE_API transform)
  keyholderUserId: string;  // Public UUID (from CAMEL_CASE_API transform)
  wearerId: number;  // Internal ID (for DB operations)
  keyholderId: number;  // Internal ID (for DB operations)
  status: 'pending_proof' | 'pending_consent' | 'pending_unlock_proof' | 'active' | 'paused' | 'completed' | 'cancelled' | 'emergency_unlocked';
  requireLockProof?: boolean; // require_lock_proof — whether proof must precede consent (from cl.* in SQL)
  lockProofUrl?: string;    // Photo proof submitted when lock started
  unlockProofUrl?: string;  // Photo proof submitted when lock ended
  originalDurationMinutes: number;
  currentDurationMinutes: number;
  remainingMinutes: number;
  progressPercent: number;
  isOverdue: boolean;
  actualStartAt?: string;
  actualEndAt?: string;
  pauseStartedAt?: string;
  totalPausedMinutes: number;
  consentGivenAt?: string;
  consentIpAddress?: string;
  keyholderNotes?: string;
  wearerNotes?: string;
  requireDailyCheckin: boolean;
  lastCheckinDate?: string;
  checkinDue: boolean;
  pendingRequestsCount: number;
  emergencyUnlockReason?: string;
  emergencyUnlockedAt?: string;
  createdAt: string;
  updatedAt: string;
  wearerName?: string;
  keyholderName?: string;
  inventoryItemId?: number;
  inventoryItemName?: string;
  healthCheckIntervalHours?: number;
  checkinPhotoMode?: 'required' | 'optional' | 'random' | 'none';
  // Timestamps for client-side countdown (from cl.* in SQL)
  currentEndTime?: string;
  commitmentEndsAt?: string;
  // Self-lock fields (Spec 069)
  isSelfLock?: boolean;
  canSelfUnlock?: boolean;
  commitmentMinutes?: number;
  commitmentRemainingMinutes?: number;
}

export interface UnlockRequest {
  id: number;
  publicId: string;
  lockId: number;
  requestType: 'temporary_hygiene' | 'permanent_unlock' | 'duration_reduction';
  reason?: string;
  status: 'pending' | 'approved' | 'denied' | 'cancelled';
  requestedReductionMinutes?: number;
  approvedReductionMinutes?: number;
  keyholderResponseNotes?: string;
  respondedAt?: string;
  autoDenyAt?: string;
  createdAt: string;
}

export interface HealthCheck {
  id: number;
  publicId: string;
  lockId: number;
  submittedByUserId: number;
  status: 'ok' | 'mild_discomfort' | 'concerning' | 'emergency';
  comfortLevel?: 1 | 2 | 3 | 4 | 5;  // Backend sends this directly
  symptoms?: string[];              // Array of symptom strings
  notes?: string;
  photoUrl?: string;                // Photo submitted with health check
  photoRequired?: boolean;          // Whether photo was required for this check
  photoVerified?: boolean;          // Whether photo was provided
  requiresAttention?: boolean;      // Flag if keyholder should review
  reviewedByUserId?: number;
  reviewedAt?: string;
  reviewerNotes?: string;
  checkDate?: string;               // Date of the check (YYYY-MM-DD)
  createdAt: string;
  // Spec 167: New health fields
  circulationIssue?: boolean;
  discomfortLevel?: 'none' | 'mild' | 'moderate' | 'severe';
}

export interface ChastityStats {
  totalLocks: number;
  completedLocks: number;
  totalTimeLockedMinutes: number;
  averageLockDurationMinutes: number;
  longestLockMinutes: number;
  emergencyUnlocks: number;
  currentStreakDays: number;
}

export interface HygieneSchedule {
  id: number;
  lockId: number;
  suggestedById: number;
  controlledById: number;
  intervalHours: number;
  isActive: boolean;
  lastReminderSentAt: string | null;
  lastCompletedAt: string | null;
  remindersSent: number;
  breaksCompleted: number;
  createdAt: string;
  updatedAt: string;
}

export interface WellnessCheck {
  id: number;
  publicId: string;
  emergencyUnlockId: number;
  comfortLevel: number | null;
  feelingOkay: boolean | null;
  notes: string | null;
  notifyKeyholder: boolean;
  promptedAt: string;
  completedAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
}

export const chastityAPI = {
  // =============================================================================
  // Locks
  // =============================================================================

  /** Get all locks for user (as wearer or keyholder) */
  getLocks: (options?: { role?: 'wearer' | 'keyholder'; status?: string }) => {
    const params = new URLSearchParams();
    if (options?.role) params.append('role', options.role);
    if (options?.status) params.append('status', options.status);
    const queryString = params.toString();
    return api.get<{ data: ChastityLock[] }>(`/chastity/locks${queryString ? `?${queryString}` : ''}`);
  },

  /** Get active lock for user as wearer */
  getActiveLock: () => api.get<{ data: ChastityLock | null }>('/chastity/locks/active'),

  /** Get a specific lock by ID */
  getLock: (lockId: number | string) =>
    api.get<{ data: ChastityLock }>(`/chastity/locks/${lockId}`),

  /** Create a new lock (keyholder only, or self-lock per Spec 069) */
  createLock: (data: {
    wearer_id: number | string;
    duration_minutes?: number;
    end_time?: string; // ISO date string for absolute end time
    keyholder_notes?: string;
    health_check_interval_hours?: number;
    inventory_item_id?: number | string;
    keyholder_ids?: string[]; // Additional keyholders (UUIDs)
    checkin_photo_mode?: 'required' | 'optional' | 'random' | 'none';
    is_self_lock?: boolean; // Spec 069: Self-lock mode
    commitment_minutes?: number; // Spec 069: Minimum commitment period (60+ min)
  }) => api.post<{ data: ChastityLock }>('/chastity/locks', data),

  /** Upload proof file (uses org-level billing, NOT Media Library billing) */
  uploadProof: (
    lockId: number | string,
    file: File,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ url: string; filename: string; size: number }>(
      `/chastity/locks/${lockId}/proof/upload`,
      formData,
      { onUploadProgress }
    );
  },

  /** Submit proof for lock start or end */
  submitProof: (
    lockId: number | string,
    data: { proof_type: 'start' | 'end'; proof_url: string }
  ) => api.post<{ data: ChastityLock }>(`/chastity/locks/${lockId}/proof`, data),

  /** Accept lock consent (wearer only) */
  acceptConsent: (lockId: number | string) =>
    api.post<{ data: ChastityLock }>(`/chastity/locks/${lockId}/consent`, {}),

  /** Update lock (extend, reduce, pause, resume, end, cancel) */
  updateLock: (
    lockId: number | string,
    data: {
      action: 'extend' | 'reduce' | 'pause' | 'resume' | 'end' | 'cancel';
      minutes?: number;
      reason?: string;
    }
  ) => api.patch<{ data: ChastityLock }>(`/chastity/locks/${lockId}`, data),

  /** Update lock settings (keyholder only) */
  updateLockSettings: (
    lockId: number | string,
    settings: {
      health_check_interval_hours?: number;
      checkin_photo_mode?: 'required' | 'optional' | 'random' | 'none';
      require_daily_checkin?: boolean;
      keyholder_notes?: string | null;
    }
  ) => api.patch<{ data: ChastityLock }>(`/chastity/locks/${lockId}/settings`, settings),

  /** Cancel a pending lock */
  cancelLock: (lockId: number | string) =>
    api.delete<{ success: boolean }>(`/chastity/locks/${lockId}`),

  /**
   * Emergency unlock - ALWAYS accessible, cannot be blocked.
   * bug-registry.md follow-up: the real backend contract is
   * { trigger_reason, details, acknowledge_immediate } -- the web UI's single
   * free-text field maps to `details`, `trigger_reason` defaults to 'other'
   * (the modal doesn't ask for a category), and `acknowledge_immediate` is
   * always true here since the modal's own warning text + explicit "Confirm
   * Emergency Unlock" button IS the acknowledgment gesture.
   */
  emergencyUnlock: (lockId: number | string, data: { details: string }) =>
    api.post<{ data: ChastityLock }>(`/chastity/locks/${lockId}/emergency-unlock`, {
      trigger_reason: 'other',
      acknowledge_immediate: true,
      details: data.details,
    }),

  /** Resume a paused lock after hygiene break */
  resumeLock: (lockId: number | string) =>
    api.post<{ data: ChastityLock }>(`/chastity/locks/${lockId}/resume`, {}),

  /** Self-unlock - for self-locks after commitment period (Spec 069) */
  selfUnlock: (lockId: number | string) =>
    api.post<{ data: ChastityLock }>(`/chastity/locks/${lockId}/self-unlock`, {}),

  // =============================================================================
  // Unlock Requests
  // =============================================================================

  /** Get pending requests for keyholder */
  getPendingRequests: () =>
    api.get<{ data: UnlockRequest[] }>('/chastity/requests/pending'),

  /** Get all requests created by the current user (across all locks) */
  getMyRequests: () =>
    api.get<{ data: UnlockRequest[]; pending_count: number }>('/chastity/requests/mine'),

  /** Get requests for a specific lock */
  getRequests: (lockId: number | string) =>
    api.get<{ data: UnlockRequest[] }>(`/chastity/locks/${lockId}/requests`),

  /** Create an unlock request (wearer only) */
  createRequest: (
    lockId: number | string,
    data: {
      request_type: 'temporary_hygiene' | 'permanent_unlock' | 'duration_reduction';
      reason?: string;
      requested_reduction_minutes?: number;
    }
  ) => api.post<{ data: UnlockRequest }>(`/chastity/locks/${lockId}/requests`, data),

  /** Respond to an unlock request (keyholder only) */
  respondToRequest: (
    requestId: number | string,
    data: {
      action: 'approve' | 'deny';
      message?: string;
      approved_duration_minutes?: number;
    }
  ) => api.patch<{ data: UnlockRequest }>(`/chastity/requests/${requestId}`, data),

  /** Withdraw a pending request */
  withdrawRequest: (requestId: number | string) =>
    api.delete<{ success: boolean }>(`/chastity/requests/${requestId}`),

  // =============================================================================
  // Health Checks
  // =============================================================================

  /** Get health alerts for keyholder */
  getHealthAlerts: () =>
    api.get<{ data: HealthCheck[] }>('/chastity/health-alerts'),

  /** Get health checks for a lock */
  getHealthChecks: (lockId: number | string) =>
    api.get<{ data: HealthCheck[] }>(`/chastity/locks/${lockId}/health-checks`),

  /** Submit a health check (wearer only) */
  createHealthCheck: (
    lockId: number | string,
    data: {
      comfort_level: 1 | 2 | 3 | 4 | 5;
      symptoms: string[];
      notes?: string;
      photo_url?: string;
      circulation_issue?: boolean;
      discomfort_level?: string;
    }
  ) => api.post<{ data: HealthCheck; photo_required: boolean }>(`/chastity/locks/${lockId}/health-checks`, data),

  /** Mark a health check as reviewed (keyholder only) */
  reviewHealthCheck: (
    checkId: number | string,
    data: { reviewer_notes?: string }
  ) => api.post<{ data: HealthCheck }>(`/chastity/health-checks/${checkId}/review`, data),

  // =============================================================================
  // Health Log & Analytics (Spec 167)
  // =============================================================================

  /** Get health log for a lock (role-aware: full for wearer, summary for keyholder) */
  getHealthLog: (lockId: number | string, options?: { days?: number }) => {
    const params = new URLSearchParams();
    if (options?.days) params.append('days', options.days.toString());
    const queryString = params.toString();
    return api.get<{
      data: {
        entries?: HealthCheck[];
        summary: {
          avgComfort: number | null;
          totalChecks: number;
          alertCount: number;
          lastCheckinAt: string | null;
          missedCount: number;
          mostCommonSymptom: string | null;
          totalLockedDays: number;
          breakCount: number;
        };
      };
    }>(`/chastity/locks/${lockId}/health-log${queryString ? `?${queryString}` : ''}`);
  },

  /** Export health log as CSV/JSON (wearer only, GDPR) */
  exportHealthLog: (lockId: number | string, format: 'csv' | 'json' = 'json') =>
    api.get(`/chastity/locks/${lockId}/health-log/export?format=${format}`, { responseType: 'blob' }),

  /** Get cross-session comfort analytics for current user */
  getHealthAnalytics: (options?: { days?: number }) => {
    const params = new URLSearchParams();
    if (options?.days) params.append('days', options.days.toString());
    const queryString = params.toString();
    return api.get<{
      data: {
        avgComfort: number | null;
        totalSessions: number;
        totalChecks: number;
        totalLockedDays: number;
        comfortTrend: Array<{ date: string; avgComfort: number }>;
        symptomFrequency: Array<{ symptom: string; count: number }>;
      };
    }>(`/chastity/health-analytics${queryString ? `?${queryString}` : ''}`);
  },

  // =============================================================================
  // Hygiene Schedule (Spec 167)
  // =============================================================================

  /** Create or update hygiene schedule for a lock */
  createHygieneSchedule: (
    lockId: number | string,
    data: { interval_hours: number }
  ) => api.post<{ data: HygieneSchedule }>(`/chastity/locks/${lockId}/hygiene-schedule`, data),

  /** Update hygiene schedule settings (wearer controls) */
  updateHygieneSchedule: (
    lockId: number | string,
    data: { interval_hours?: number; is_active?: boolean }
  ) => api.patch<{ data: HygieneSchedule }>(`/chastity/locks/${lockId}/hygiene-schedule`, data),

  /** Log a completed hygiene break */
  logHygieneBreak: (
    lockId: number | string,
    data: { pause_lock?: boolean }
  ) => api.post<{ data: { message: string } }>(`/chastity/locks/${lockId}/hygiene-break`, data),

  // =============================================================================
  // Wellness Check (Spec 167)
  // =============================================================================

  /** Submit post-emergency wellness check */
  submitWellnessCheck: (
    emergencyUnlockId: string,
    data: {
      comfort_level?: number;
      feeling_okay?: boolean;
      notes?: string;
      notify_keyholder?: boolean;
    }
  ) => api.post<{ data: WellnessCheck }>(`/chastity/wellness-check/${emergencyUnlockId}`, data),

  /** Get wellness check status for an emergency unlock */
  getWellnessCheck: (emergencyUnlockId: string) =>
    api.get<{ data: WellnessCheck | null }>(`/chastity/wellness-check/${emergencyUnlockId}`),

  // =============================================================================
  // Stats & History
  // =============================================================================

  /** Get user stats */
  getStats: (role?: 'wearer' | 'keyholder') => {
    const params = role ? `?role=${role}` : '';
    return api.get<{ data: ChastityStats }>(`/chastity/stats${params}`);
  },

  /** Get lock history */
  getHistory: (options?: { role?: 'wearer' | 'keyholder'; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.role) params.append('role', options.role);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    const queryString = params.toString();
    return api.get<{
      data: ChastityLock[];
      pagination: { total: number; page: number; limit: number };
    }>(`/chastity/history${queryString ? `?${queryString}` : ''}`);
  },
};
